/**
 * Reading the "CNH | Validade" custom field.
 *
 * The field is free text a customer defined and an operator types as DD/MM/AAAA, and nothing
 * ever looked at it — an expired licence was stored exactly like a valid one, leaving the date
 * arithmetic to whoever was standing at the gate. This turns it into the three answers that
 * lead to three different actions: long expired, inside the renewal window (either side of the
 * date), fine.
 *
 * Front-end only, and deliberately advisory: it never blocks a registration. Whether to let
 * someone drive in on an expired licence is the condomínio's call, not the form's.
 */

/**
 * The warning window, applied on BOTH sides of the expiry date.
 *
 * Before it: the licence is about to run out. After it: the licence is renewable and people
 * drive on the old document while the renewal is processed — expired yesterday is not the
 * same fact as expired last year, and treating them alike made the loud state meaningless.
 */
export const CNH_WARNING_DAYS = 30;

export type CnhValidadeStatus = 'vencida' | 'vence_em_breve' | 'valida';

export interface CnhValidadeCheck {
  status: CnhValidadeStatus;
  /** Negative once expired; 0 on the last valid day. */
  daysUntilExpiry: number;
  /** Days of tolerance still left, only while the licence is past its date but inside it. */
  toleranceDaysLeft?: number;
  message: string;
}

/**
 * Does this custom field hold a CNH expiry?
 *
 * Matched on the LABEL because the field is customer-defined — there is no id to key on, and
 * each condomínio spells it its own way ("CNH | Validade", "CNH - Validade", "Validade da CNH").
 * "CNH | Categoria" must not match: it sits right beside it and holds "B", not a date.
 */
export function isCnhValidadeField(label?: string | null): boolean {
  if (!label) return false;
  const normalized = label
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
  return normalized.includes('cnh') && /\bvalidade\b/.test(normalized);
}

/** Midnight of the given day, so comparisons are date-to-date and not clock-to-clock. */
function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Parses DD/MM/AAAA (what the mask produces) or AAAA-MM-DD (what the API stores).
 * Returns null for anything incomplete or impossible — 31/02 is not a date.
 */
function parseDate(raw: string): Date | null {
  const text = raw.trim();

  const br = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!br && !iso) return null;

  const [day, month, year] = br
    ? [Number(br[1]), Number(br[2]), Number(br[3])]
    : [Number(iso![3]), Number(iso![2]), Number(iso![1])];

  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) return null;

  const date = new Date(year, month - 1, day);
  // Rolls over for impossible days (31/02 becomes 03/03), which is how we reject them.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function plural(days: number): string {
  return days === 1 ? '1 dia' : `${days} dias`;
}

/**
 * The state of a typed CNH expiry, or null while there is nothing to judge (partial date,
 * impossible date, empty field) — a hint that flashes "vencida" halfway through typing is a
 * hint people learn to ignore.
 */
export function checkCnhValidade(value?: string | null, today: Date = new Date()): CnhValidadeCheck | null {
  if (!value) return null;
  const expiry = parseDate(value);
  if (!expiry) return null;

  const daysUntilExpiry = Math.round((startOfDay(expiry).getTime() - startOfDay(today).getTime()) / MS_PER_DAY);

  // Past the window on the expired side: no longer a renewal in progress, just an invalid
  // licence.
  if (daysUntilExpiry < -CNH_WARNING_DAYS) {
    return {
      status: 'vencida',
      daysUntilExpiry,
      message: `CNH vencida há ${plural(Math.abs(daysUntilExpiry))}`,
    };
  }

  // Past the date but inside the window. One line, three facts, in the order the porteiro
  // needs them: the verdict (they can let the car in), why it is not a clean yes, and the
  // deadline they are working against — "vencida há 11 dias" alone answers none of that.
  if (daysUntilExpiry < 0) {
    const daysOverdue = Math.abs(daysUntilExpiry);
    const toleranceDaysLeft = CNH_WARNING_DAYS - daysOverdue;
    return {
      status: 'vence_em_breve',
      daysUntilExpiry,
      toleranceDaysLeft,
      message: toleranceDaysLeft === 0
        ? `CNH ainda válida — vencida há ${plural(daysOverdue)}, hoje é o último dia de tolerância`
        : `CNH ainda válida — vencida há ${plural(daysOverdue)}, restam ${plural(toleranceDaysLeft)} de tolerância`,
    };
  }

  // A licence is valid THROUGH its expiry date, so 0 is still valid — and still worth warning
  // about, since it stops being valid tomorrow.
  if (daysUntilExpiry <= CNH_WARNING_DAYS) {
    return {
      status: 'vence_em_breve',
      daysUntilExpiry,
      message: daysUntilExpiry === 0 ? 'CNH vence hoje' : `CNH vence em ${plural(daysUntilExpiry)}`,
    };
  }

  return {
    status: 'valida',
    daysUntilExpiry,
    message: 'CNH válida',
  };
}
